/** Shared UI kit — aeGov Design System components with a scenic desert backdrop. */
import type {
  ButtonHTMLAttributes,
  HTMLAttributes,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactNode,
  SelectHTMLAttributes,
  TextareaHTMLAttributes,
} from 'react';
import { useState } from 'react';
import { useI18n, type Lang } from './i18n.js';

function cx(...parts: (string | false | null | undefined)[]): string {
  return parts.filter(Boolean).join(' ');
}

// ---------------------------------------------------------------------------
// Scenic bottom layer — the identity element (single desert dunes panorama)
// ---------------------------------------------------------------------------

/** @deprecated Kept for call-site compatibility; every page uses dunes-dawn. */
export type SceneName = 'dunes-dawn' | 'hajar-road' | 'mangrove' | 'energy-dusk';

const DESERT_SKY = 'linear-gradient(to bottom, #f6f1e7 0%, #f9ecd9 55%, #f6d9b4 100%)';
const DESERT_SCRIM = '#f6f1e7D9'; // ~85% alpha of the dunes sky edge

/**
 * The desert panorama every surface floats its glass over.
 *
 * ## Why `src` is a prop now
 *
 * The photograph lives once, in the portal's own `public/`. The embedded
 * assistant is a different origin and reaches it through the platform's asset
 * route, so it passes the resolved URL; a hard-coded root-relative path
 * requested the ministry's own homepage and got its 404 page back as an image.
 *
 * ## Why the panel does not get a full-bleed one
 *
 * `band` covers the top of its container rather than the whole of it and fades
 * into the surface below. Inside a 480px docked panel a full photograph behind a
 * hundred-and-nineteen-row service list is a postcard somebody has printed a
 * form on: glass needs something to refract, and the top of the panel is where
 * the header, the greeting and the search field are — everything below is a list
 * that wants to be read. `fixed` is the page treatment and stays the default.
 */
export function ScenicBackdrop({
  src = '/scenes/scene-dunes-dawn.png',
  variant = 'fixed',
}: {
  scene?: SceneName;
  height?: string;
  /** Where the photograph is served from on this surface. */
  src?: string;
  variant?: 'fixed' | 'band';
} = {}) {
  if (variant === 'band') {
    return (
      <div aria-hidden className="pointer-events-none absolute inset-x-0 top-0 h-[42%] overflow-hidden">
        <div className="absolute inset-0" style={{ background: DESERT_SKY }} />
        <img
          src={src}
          alt=""
          className="absolute inset-0 h-full w-full object-cover object-[center_78%]"
        />
        {/* Down into the panel's own surface rather than to transparent: the
            band has a bottom edge and a photograph that simply stops is a seam. */}
        <div
          className="absolute inset-0"
          style={{
            background: `linear-gradient(to bottom, ${DESERT_SCRIM} 0%, #f6f1e799 34%, var(--color-whitely-50) 100%)`,
          }}
        />
      </div>
    );
  }

  return (
    <div aria-hidden className="pointer-events-none fixed inset-0 -z-10 overflow-hidden">
      <div className="absolute inset-0" style={{ background: DESERT_SKY }} />
      <img
        src={src}
        alt=""
        className="absolute inset-0 h-full w-full object-cover object-[center_72%]"
      />
      <div
        className="absolute inset-0"
        style={{
          background: `linear-gradient(to bottom, ${DESERT_SCRIM} 0%, transparent 28%, transparent 62%, ${DESERT_SCRIM} 100%)`,
        }}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Surfaces — DLS `.aegov-card` by default; scenic glass opt-in via `variant`
// ---------------------------------------------------------------------------

export function GlassCard({
  children,
  className,
  strong,
  dim,
  variant,
  ...props
}: {
  children: ReactNode;
  className?: string;
  /** @deprecated Prefer variant="glass" / "glass-strong". */
  strong?: boolean;
  /** @deprecated Prefer variant="glass-dim". */
  dim?: boolean;
  variant?: 'dls' | 'glass' | 'glass-strong' | 'glass-dim' | 'service';
} & HTMLAttributes<HTMLDivElement>) {
  const resolved =
    variant ?? (dim ? 'glass-dim' : strong ? 'glass-strong' : 'dls');

  if (resolved === 'glass' || resolved === 'glass-strong' || resolved === 'glass-dim') {
    return (
      <div
        {...props}
        className={cx(
          resolved === 'glass-dim' ? 'glass-dim' : resolved === 'glass-strong' ? 'glass-strong' : 'glass',
          'rounded-(--radius-glass)',
          className,
        )}
      >
        {children}
      </div>
    );
  }

  return (
    <div
      {...props}
      className={cx(
        // card-compact keeps call-site padding (DLS card-bordered defaults to p-6).
        'aegov-card card-bordered card-compact bg-whitely-50/95 backdrop-blur-sm',
        resolved === 'service' && 'card-service',
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * Buttons render the DLS `.aegov-btn` component for every variant:
 * primary / danger / secondary-soft (former "glass") / outline (former "ghost").
 * Sizes map onto the DLS scale (md → btn-sm, sm → btn-xs) with the fixed
 * height relaxed to a min-height so long bilingual labels can wrap.
 */
export function GlassButton({
  children,
  variant = 'glass',
  size = 'md',
  className,
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: 'glass' | 'primary' | 'danger' | 'ghost';
  size?: 'sm' | 'md';
}) {
  const base = 'inline-flex min-w-0 max-w-full items-center justify-center gap-2 align-middle text-center whitespace-normal leading-tight';
  const sizeClass = size === 'md' ? 'btn-sm min-h-10 py-1' : 'btn-xs min-h-10 py-1';
  const variantClass =
    variant === 'primary'
      ? ''
      : variant === 'danger'
        ? 'btn-danger'
        : variant === 'ghost'
          ? 'btn-outline'
          : 'btn-secondary btn-soft';

  return (
    <button
      className={cx('aegov-btn h-auto', sizeClass, variantClass, base, className)}
      {...props}
    >
      {children}
    </button>
  );
}

/** Text input rendered as the DLS `.aegov-form-control` component (control-sm
 *  scale): white field wrapped by the 2px primary inset ring that switches to
 *  the camel focus ring, placeholder in aeblack-500. `className` styles the
 *  control wrapper; all other props reach the native input. */
export function GlassInput({ className, type = 'text', ...props }: InputHTMLAttributes<HTMLInputElement>) {
  return (
    <div className={cx('aegov-form-control control-sm', className)}>
      <div className="form-control-input">
        <input type={type} {...props} />
      </div>
    </div>
  );
}

/** Select rendered through the same DLS shell and control geometry as inputs. */
export function GlassSelect({
  className,
  children,
  ...props
}: SelectHTMLAttributes<HTMLSelectElement>) {
  return (
    <div className={cx('aegov-form-control control-sm', className)}>
      <div className="form-control-input">
        <select {...props}>{children}</select>
      </div>
    </div>
  );
}

/** Textarea rendered as the DLS `.aegov-form-control` component. */
export function GlassTextarea({
  className,
  ...props
}: TextareaHTMLAttributes<HTMLTextAreaElement>) {
  return (
    <div className={cx('aegov-form-control control-sm', className)}>
      <div className="form-control-input">
        <textarea {...props} />
      </div>
    </div>
  );
}

/** Filter / category chip — DLS secondary soft button with an active outline. */
export function Chip({
  children,
  onClick,
  active,
  size = 'md',
  className,
}: {
  children: ReactNode;
  onClick?: () => void;
  active?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cx(
        'aegov-btn btn-secondary btn-soft inline-flex h-auto max-w-full items-center justify-center gap-2 align-middle text-center whitespace-normal leading-tight',
        size === 'md' ? 'btn-sm min-h-10 py-1' : 'btn-xs min-h-10 py-1',
        active && 'ring-2 ring-primary-600 ring-offset-1',
        className,
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Status + language
// ---------------------------------------------------------------------------

/** Case states mapped onto the DLS badge variants: green for terminal
 *  success, red for terminal failure, camel (warning) for anything waiting
 *  on a human, seablue (info) for active work, primary tint for drafts. */
const STATE_VARIANT: Record<string, string> = {
  approved: 'badge-success',
  executed: 'badge-success',
  published: 'badge-success',
  rejected: 'badge-error',
  escalated: 'badge-error',
  in_review: 'badge-warning',
  submitted: 'badge-warning',
  queued: 'badge-warning',
  pending: 'badge-warning',
  needs_citizen_input: 'badge-warning',
  returned: 'badge-warning',
  in_progress: 'badge-info',
  draft: '',
  // The three ways an application ends without a decision to celebrate. They
  // fell through to the amber default, which is the colour of "still going" —
  // so a delivered permit and a withdrawn request both read as in progress.
  delivered: 'badge-success',
  withdrawn: '',
  expired: 'badge-error',
};

/** Status badge rendered as the DLS `.aegov-badge` component. */
export function StatusPill({ state, label, className }: { state: string; label: string; className?: string }) {
  return <span className={cx('aegov-badge', STATE_VARIANT[state] ?? 'badge-warning', className)}>{label}</span>;
}

export function LangToggle({ className }: { className?: string }) {
  const { lang, setLang } = useI18n();
  const next: Lang = lang === 'ar' ? 'en' : 'ar';
  return (
    <button
      type="button"
      onClick={() => setLang(next)}
      className={cx('aegov-btn btn-secondary btn-soft btn-xs inline-flex min-h-10 items-center justify-center font-semibold', className)}
      aria-label={next === 'ar' ? 'التبديل إلى العربية' : 'Switch to English'}
    >
      {next === 'ar' ? 'العربية' : 'English'}
    </button>
  );
}

export function TypingDots() {
  return (
    <span className="inline-flex items-center gap-1 px-1" aria-label="typing">
      {[0, 1, 2].map((i) => (
        <span
          key={i}
          className="animate-typing-dot inline-block h-1.5 w-1.5 rounded-full bg-ink-soft"
          style={{ animationDelay: `${i * 0.18}s` }}
        />
      ))}
    </span>
  );
}

export function EmptyState({ title, hint }: { title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center gap-1 py-14 text-center">
      <div className="text-sm font-semibold text-ink-soft">{title}</div>
      {hint && <div className="text-xs text-ink-faint">{hint}</div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Enterprise console primitives
// ---------------------------------------------------------------------------

export interface BreadcrumbItem {
  label: string;
  href?: string;
}

/** Consistent title, breadcrumb and action region for dense console pages. */
export function PageHeader({
  title,
  subtitle,
  breadcrumbs,
  actions,
  status,
}: {
  title: string;
  subtitle?: string;
  breadcrumbs?: BreadcrumbItem[];
  actions?: ReactNode;
  status?: ReactNode;
}) {
  return (
    <header className="mb-5 border-b border-aeblack-100 pb-4">
      {breadcrumbs && breadcrumbs.length > 0 && (
        <nav aria-label="Breadcrumb" className="mb-2 text-xs text-ink-faint">
          <ol className="flex flex-wrap items-center gap-1.5">
            {breadcrumbs.map((item, index) => (
              <li key={`${item.label}-${index}`} className="flex items-center gap-1.5">
                {index > 0 && <span aria-hidden>/</span>}
                {item.href ? (
                  <a href={item.href} className="font-semibold text-gold-deep hover:underline">
                    {item.label}
                  </a>
                ) : (
                  <span aria-current={index === breadcrumbs.length - 1 ? 'page' : undefined}>{item.label}</span>
                )}
              </li>
            ))}
          </ol>
        </nav>
      )}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <h1 className="text-2xl font-extrabold tracking-tight sm:text-[2rem]">{title}</h1>
            {status}
          </div>
          {subtitle && <p className="mt-1 max-w-5xl text-sm leading-relaxed text-ink-soft">{subtitle}</p>}
        </div>
        {actions && <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:shrink-0 sm:justify-end">{actions}</div>}
      </div>
    </header>
  );
}

/** Toolbar container shared by table search, filters and saved views. */
export function FilterBar({
  children,
  summary,
  className,
}: {
  children: ReactNode;
  summary?: ReactNode;
  className?: string;
}) {
  return (
    <div
      className={cx(
        'mb-3 flex flex-wrap items-center gap-2 rounded-(--radius-control) border border-aeblack-100 bg-white p-3',
        className,
      )}
    >
      {children}
      {summary && <div className="w-full text-xs font-semibold text-ink-faint sm:ms-auto sm:w-auto">{summary}</div>}
    </div>
  );
}

export interface DetailSection {
  id: string;
  label: string;
  description?: string;
  badge?: ReactNode;
}

/** Left section navigation with a stable content plane and optional inspector. */
export function DetailLayout({
  sections,
  activeSection,
  onSectionChange,
  children,
  inspector,
}: {
  sections: DetailSection[];
  activeSection: string;
  onSectionChange: (section: string) => void;
  children: ReactNode;
  inspector?: ReactNode;
}) {
  return (
    <div className={cx('grid items-start gap-4', inspector ? 'xl:grid-cols-[13rem_minmax(0,1fr)_18rem]' : 'lg:grid-cols-[13rem_minmax(0,1fr)]')}>
      <nav aria-label="Detail sections" className="sticky top-24 rounded-(--radius-control) border border-aeblack-100 bg-white p-2">
        {sections.map((section) => (
          <button
            key={section.id}
            type="button"
            onClick={() => onSectionChange(section.id)}
            aria-current={activeSection === section.id ? 'page' : undefined}
            className={cx(
              'flex w-full items-start gap-2 rounded-md px-3 py-2 text-start text-xs transition-colors',
              activeSection === section.id
                ? 'bg-aeblack-800 font-bold text-white'
                : 'font-semibold text-ink-soft hover:bg-aeblack-50 hover:text-ink',
            )}
          >
            <span className="min-w-0 flex-1">
              <span className="block">{section.label}</span>
              {section.description && (
                <span className={cx('mt-0.5 block text-[10px] font-normal', activeSection === section.id ? 'text-aeblack-200' : 'text-ink-faint')}>
                  {section.description}
                </span>
              )}
            </span>
            {section.badge}
          </button>
        ))}
      </nav>
      <section className="min-w-0">{children}</section>
      {inspector && (
        <aside className="sticky top-24 rounded-(--radius-control) border border-aeblack-100 bg-white p-4">
          {inspector}
        </aside>
      )}
    </div>
  );
}

export interface DataTableColumn<T> {
  id: string;
  header: string;
  render: (row: T) => ReactNode;
  value?: (row: T) => string | number | null | undefined;
  sortable?: boolean;
  align?: 'start' | 'center' | 'end';
  className?: string;
}

function csvCell(value: unknown): string {
  const text = value === null || value === undefined ? '' : String(value);
  return `"${text.replaceAll('"', '""')}"`;
}

/** Accessible sortable/paginated table for console registries and queues. */
export function DataTable<T>({
  rows,
  columns,
  rowKey,
  loading,
  emptyTitle = 'No records',
  emptyHint,
  pageSize = 20,
  filename,
  onExport,
  onRowClick,
  selectedKeys,
  onSelectionChange,
}: {
  rows: T[];
  columns: DataTableColumn<T>[];
  rowKey: (row: T) => string;
  loading?: boolean;
  emptyTitle?: string;
  emptyHint?: string;
  pageSize?: number;
  filename?: string;
  onExport?: () => void | Promise<void>;
  onRowClick?: (row: T) => void;
  selectedKeys?: string[];
  onSelectionChange?: (keys: string[]) => void;
}) {
  const [sort, setSort] = useState<{ id: string; direction: 'asc' | 'desc' } | null>(null);
  const [page, setPage] = useState(0);
  const sorted = [...rows];
  if (sort) {
    const column = columns.find((candidate) => candidate.id === sort.id);
    if (column?.value) {
      sorted.sort((left, right) => {
        const a = column.value?.(left);
        const b = column.value?.(right);
        const comparison = String(a ?? '').localeCompare(String(b ?? ''), undefined, { numeric: true });
        return sort.direction === 'asc' ? comparison : -comparison;
      });
    }
  }
  const pages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(page, pages - 1);
  const visible = sorted.slice(safePage * pageSize, safePage * pageSize + pageSize);
  const selectable = Boolean(onSelectionChange);
  const selected = new Set(selectedKeys ?? []);

  const toggleSort = (column: DataTableColumn<T>) => {
    if (!column.sortable || !column.value) return;
    setPage(0);
    setSort((current) =>
      current?.id === column.id
        ? { id: column.id, direction: current.direction === 'asc' ? 'desc' : 'asc' }
        : { id: column.id, direction: 'asc' },
    );
  };

  const exportRows = async () => {
    if (onExport) {
      await onExport();
      return;
    }
    const csv = [
      columns.map((column) => csvCell(column.header)).join(','),
      ...sorted.map((row) => columns.map((column) => csvCell(column.value?.(row) ?? '')).join(',')),
    ].join('\n');
    const url = URL.createObjectURL(new Blob([`\uFEFF${csv}`], { type: 'text/csv;charset=utf-8' }));
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = filename ?? 'export.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const activateRow = (event: KeyboardEvent<HTMLTableRowElement>, row: T) => {
    if (!onRowClick || (event.key !== 'Enter' && event.key !== ' ')) return;
    event.preventDefault();
    onRowClick(row);
  };

  if (loading) {
    return (
      <div className="overflow-hidden rounded-(--radius-control) border border-aeblack-100 bg-white" aria-busy="true">
        {[0, 1, 2, 3, 4].map((row) => (
          <div key={row} className="flex gap-4 border-b border-aeblack-50 p-4 last:border-0">
            <span className="h-4 w-1/4 animate-pulse rounded bg-aeblack-100" />
            <span className="h-4 w-1/3 animate-pulse rounded bg-aeblack-50" />
            <span className="h-4 w-1/6 animate-pulse rounded bg-aeblack-50" />
          </div>
        ))}
      </div>
    );
  }

  if (rows.length === 0) {
    return (
      <div className="rounded-(--radius-control) border border-aeblack-100 bg-white">
        <EmptyState title={emptyTitle} hint={emptyHint} />
      </div>
    );
  }

  return (
    <div className="overflow-hidden rounded-(--radius-control) border border-aeblack-100 bg-white">
      <div className="overflow-x-auto">
        <table className="w-full border-collapse text-sm">
          <thead className="sticky top-0 z-10 bg-aeblack-50 text-[11px] tracking-wide text-ink-faint uppercase">
            <tr>
              {selectable && (
                <th scope="col" className="w-10 border-b border-aeblack-100 p-3 text-start">
                  <input
                    type="checkbox"
                    aria-label="Select all rows"
                    className="h-4 w-4 align-middle"
                    checked={visible.length > 0 && visible.every((row) => selected.has(rowKey(row)))}
                    onChange={(event) => {
                      const visibleKeys = visible.map(rowKey);
                      onSelectionChange?.(
                        event.target.checked
                          ? [...new Set([...selected, ...visibleKeys])]
                          : [...selected].filter((key) => !visibleKeys.includes(key)),
                      );
                    }}
                  />
                </th>
              )}
              {columns.map((column) => (
                <th
                  key={column.id}
                  scope="col"
                  aria-sort={sort?.id === column.id ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
                  className={cx(
                    'border-b border-aeblack-100 p-3',
                    column.align === 'center' ? 'text-center' : column.align === 'end' ? 'text-end' : 'text-start',
                    column.className,
                  )}
                >
                  <button
                    type="button"
                    disabled={!column.sortable || !column.value}
                    onClick={() => toggleSort(column)}
                    className="inline-flex min-h-8 items-center justify-center gap-1 align-middle font-bold leading-none disabled:cursor-default"
                  >
                    {column.header}
                    {column.sortable && (
                      <svg
                        aria-hidden
                        viewBox="0 0 16 16"
                        className="h-3.5 w-3.5 shrink-0"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      >
                        {sort?.id === column.id ? (
                          sort.direction === 'asc'
                            ? <path d="m4.5 9.5 3.5-4 3.5 4" />
                            : <path d="m4.5 6.5 3.5 4 3.5-4" />
                        ) : (
                          <>
                            <path d="m4.5 6.5 3.5-4 3.5 4" />
                            <path d="m4.5 9.5 3.5 4 3.5-4" />
                          </>
                        )}
                      </svg>
                    )}
                  </button>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {visible.map((row) => {
              const key = rowKey(row);
              return (
                <tr
                  key={key}
                  tabIndex={onRowClick ? 0 : undefined}
                  onClick={() => onRowClick?.(row)}
                  onKeyDown={(event) => activateRow(event, row)}
                  className={cx(
                    'border-b border-aeblack-50 last:border-0',
                    onRowClick && 'cursor-pointer hover:bg-aegold-50/60 focus:bg-aegold-50/60',
                  )}
                >
                  {selectable && (
                    <td className="p-3" onClick={(event) => event.stopPropagation()}>
                      <input
                        type="checkbox"
                        aria-label={`Select row ${key}`}
                        className="h-4 w-4 align-middle"
                        checked={selected.has(key)}
                        onChange={(event) =>
                          onSelectionChange?.(
                            event.target.checked
                              ? [...selected, key]
                              : [...selected].filter((selectedKey) => selectedKey !== key),
                          )
                        }
                      />
                    </td>
                  )}
                  {columns.map((column) => (
                    <td
                      key={column.id}
                      className={cx(
                        'p-3 align-middle',
                        column.align === 'center' ? 'text-center' : column.align === 'end' ? 'text-end' : 'text-start',
                        column.className,
                      )}
                    >
                      {column.render(row)}
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <footer className="flex flex-wrap items-center justify-between gap-2 border-t border-aeblack-100 bg-aeblack-50/70 px-3 py-2 text-xs text-ink-faint">
        <span>
          {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, sorted.length)} of {sorted.length}
        </span>
        <div className="flex items-center gap-2">
          {(filename || onExport) && (
            <button type="button" onClick={() => void exportRows()} className="inline-flex min-h-8 items-center font-bold text-gold-deep hover:underline">
              Export CSV
            </button>
          )}
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setPage(Math.max(0, safePage - 1))}
            className="inline-flex min-h-8 items-center justify-center rounded border border-aeblack-100 bg-white px-2 py-1 font-semibold disabled:opacity-40"
          >
            Previous
          </button>
          <span className="font-mono">{safePage + 1}/{pages}</span>
          <button
            type="button"
            disabled={safePage >= pages - 1}
            onClick={() => setPage(Math.min(pages - 1, safePage + 1))}
            className="inline-flex min-h-8 items-center justify-center rounded border border-aeblack-100 bg-white px-2 py-1 font-semibold disabled:opacity-40"
          >
            Next
          </button>
        </div>
      </footer>
    </div>
  );
}

export function MetricCard({
  label,
  value,
  hint,
  tone = 'neutral',
}: {
  label: string;
  value: ReactNode;
  hint?: string;
  tone?: 'neutral' | 'info' | 'success' | 'warning' | 'danger';
}) {
  const toneClass = {
    neutral: 'text-ink',
    info: 'text-oasis',
    success: 'text-approve',
    warning: 'text-gold-deep',
    danger: 'text-reject',
  }[tone];
  return (
    <div className="rounded-(--radius-control) border border-aeblack-100 bg-white p-4">
      <div className="text-[11px] font-bold tracking-wide text-ink-faint uppercase">{label}</div>
      <div className={cx('mt-1 font-mono text-2xl font-extrabold', toneClass)}>{value}</div>
      {hint && <div className="mt-1 text-xs text-ink-faint">{hint}</div>}
    </div>
  );
}
